import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, RotateCcw, Scissors, Wand2, X } from 'lucide-react';

// Sep 2026 — Zuschnitt-Editor nach dem Foto: erkannte Ecken als Vorschlag, alle 4 Ecken
// per Finger verschiebbar. Ecken normiert 0..1 (Reihenfolge tl, tr, br, bl).
const DEFAULT_QUAD = [[0.06, 0.06], [0.94, 0.06], [0.94, 0.94], [0.06, 0.94]];
const HANDLE_R = 22; // Touch-Zielgröße

export default function ReceiptCropEditor({ imageUrl, initialQuad, detected, busy, onConfirm, onSkip, onRetake, onRedetect }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [quad, setQuad] = useState(initialQuad || DEFAULT_QUAD);
  const [rect, setRect] = useState(null); // dargestellter Bildbereich (object-contain) in px
  const dragRef = useRef(null); // { idx, pointerId }

  useEffect(() => { setQuad(initialQuad || DEFAULT_QUAD); }, [initialQuad]);

  const measure = () => {
    const w = wrapRef.current; const im = imgRef.current;
    if (!w || !im || !im.naturalWidth) return;
    const cw = w.clientWidth; const ch = w.clientHeight;
    const s = Math.min(cw / im.naturalWidth, ch / im.naturalHeight);
    const dw = im.naturalWidth * s; const dh = im.naturalHeight * s;
    setRect({ x: (cw - dw) / 2, y: (ch - dh) / 2, w: dw, h: dh });
  };
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const toPx = ([nx, ny]) => [rect.x + nx * rect.w, rect.y + ny * rect.h];
  const toNorm = (px, py) => [
    Math.min(1, Math.max(0, (px - rect.x) / rect.w)),
    Math.min(1, Math.max(0, (py - rect.y) / rect.h)),
  ];

  const onPointerDown = (idx) => (e) => {
    e.preventDefault();
    dragRef.current = { idx, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !rect) return;
    const box = wrapRef.current.getBoundingClientRect();
    const [nx, ny] = toNorm(e.clientX - box.left, e.clientY - box.top);
    setQuad(prev => prev.map((p, i) => (i === d.idx ? [nx, ny] : p)));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const pts = rect ? quad.map(toPx) : null;
  const changed = JSON.stringify(quad) !== JSON.stringify(initialQuad || DEFAULT_QUAD);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950 text-white" data-testid="scan-crop-editor">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <button type="button" onClick={onRetake} className="p-2 -ml-2 text-slate-300" data-testid="scan-crop-retake" aria-label="Neu fotografieren">
          <X className="h-5 w-5" />
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold">Zuschnitt prüfen</div>
          <div className={`text-[11px] ${detected ? 'text-emerald-400' : 'text-amber-300'}`} data-testid="scan-crop-status">
            {detected ? 'Beleg erkannt — Ecken bei Bedarf verschieben' : 'Kein Beleg erkannt — Ecken manuell auf den Beleg ziehen'}
          </div>
        </div>
        <button type="button" onClick={onRedetect} disabled={busy} className="p-2 -mr-2 text-slate-300 disabled:opacity-40"
          data-testid="scan-crop-redetect" aria-label="Ecken automatisch erkennen" title="Ecken automatisch erkennen">
          <Wand2 className="h-5 w-5" />
        </button>
      </div>

      <div
        ref={wrapRef}
        className="relative flex-1 min-h-0 touch-none select-none"
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="scan-crop-canvas"
      >
        <img ref={imgRef} src={imageUrl} alt="Aufgenommener Beleg" onLoad={measure}
          className="absolute inset-0 w-full h-full object-contain pointer-events-none" draggable={false} />
        {pts && (
          <svg className="absolute inset-0 w-full h-full" data-testid="scan-crop-overlay">
            <defs>
              <mask id="crop-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
                <polygon points={pts.map(p => p.join(',')).join(' ')} fill="#000" />
              </mask>
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#crop-mask)" />
            <polygon points={pts.map(p => p.join(',')).join(' ')} fill="rgba(16,185,129,0.08)" stroke="#34d399" strokeWidth="2.5" strokeLinejoin="round" />
            {pts.map((p, i) => (
              <g key={i} onPointerDown={onPointerDown(i)} style={{ cursor: 'grab' }} data-testid={`scan-crop-handle-${i}`}>
                <circle cx={p[0]} cy={p[1]} r={HANDLE_R} fill="rgba(52,211,153,0.18)" />
                <circle cx={p[0]} cy={p[1]} r="9" fill="#34d399" stroke="#fff" strokeWidth="2.5" />
              </g>
            ))}
          </svg>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-testid="scan-crop-busy">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        )}
      </div>

      <div className="px-4 pt-3 pb-6 border-t border-slate-800 space-y-2">
        <button
          type="button"
          onClick={() => onConfirm(quad)}
          disabled={busy}
          className="w-full h-12 rounded-xl bg-emerald-500 text-slate-950 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition"
          data-testid="scan-crop-confirm"
        >
          <Scissors className="h-4 w-4" /> Zuschneiden & übernehmen
        </button>
        <div className="flex gap-2">
          <button type="button" onClick={onSkip} disabled={busy}
            className="flex-1 h-10 rounded-xl border border-slate-700 text-slate-200 text-xs font-medium disabled:opacity-50"
            data-testid="scan-crop-skip">
            <Check className="inline h-3.5 w-3.5 mr-1" /> Ohne Zuschnitt übernehmen
          </button>
          <button type="button" onClick={() => setQuad(initialQuad || DEFAULT_QUAD)} disabled={busy || !changed}
            className="h-10 px-3 rounded-xl border border-slate-700 text-slate-300 text-xs font-medium disabled:opacity-40"
            data-testid="scan-crop-reset" title="Ecken zurücksetzen">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
